/**
 * Types for workflow tuning — `POST /auto-tune/workflow/*` (AIT-138).
 *
 * The config-tuning twin of these routes improves one agent config; these improve the
 * target system's whole state graph, one state per internal iteration. Two properties
 * shape everything below:
 *
 *  - the request carries **no config** — the graph comes from a pinned snapshot;
 *  - the result is a *proposal*, not a write. The backend never touches the live graph;
 *    the frontend applies it through the connector's admin surface against
 *    `base_revision.digest` (see docs/WORKFLOW_API.md).
 */

import type { EvaluationMode } from './evaluation';

export type WorkflowTuningApproach = 'BASELINE' | 'PACE' | 'MULTISTAGE-PACE';

export type WorkflowTuningJobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelling'
  | 'cancelled';

// ─── Request ────────────────────────────────────────────────────────────────

/** Only these keys are read; anything else falls back to service defaults. */
export interface WorkflowTrainConfig {
  batch_size?: number;
  train_batches?: number;
  validation_batches?: number;
  /** Number of variants — `MULTISTAGE-PACE` only, and each variant runs every slice. */
  n_prompts_to_generate?: number;
  max_user_turns?: number;
  max_workers?: number;
}

/** `null` means no limit — the backend never invents one. */
export interface WorkflowScopeLimits {
  max_states?: number | null;
  max_roles?: number | null;
  max_cards?: number | null;
  max_global_sections?: number | null;
}

export interface WorkflowScope {
  /** Empty = every state in the graph. An unknown id is rejected with `400
   *  workflow_state_unknown` since AIT-294 — it is no longer dropped silently. */
  state_ids?: string[];
  /** >1 is a different mode, not the same run faster: parallel slices all see the pre-step graph. */
  parallel_iterations?: number;
  allow_format_requirements?: boolean;
  include_ontology?: boolean;
  include_knowledge_base?: boolean;
  read_only_context?: boolean;
  /** Above zero, a state's workers are rewritten as a whole list instead of per field. */
  container_growth_headroom?: number;
  limits?: WorkflowScopeLimits;
}

export interface WorkflowStateMachineSpec {
  /** Empty or absent → `dev`. */
  stand?: string;
  workflow: WorkflowScope;
  /** Start from explicit values instead of the snapshot; keys are canonical `resolved_targets` paths. */
  initial_overrides?: Record<string, unknown> | null;
  max_runtime_turns?: number | null;
  max_projection_update_tokens?: number | null;
  /** Empty = no per-turn retries. */
  actor_retry_delays_s?: number[];
  max_algorithm_iterations?: number;
}

export interface WorkflowTuningJobRequest {
  integration_id: string;
  dataset_name: string | string[];
  approach?: WorkflowTuningApproach;
  /** Defaults to `openai-similarity` server-side — NOT `bert_score`. */
  evaluation_mode?: EvaluationMode | string;
  evaluator_params?: Record<string, unknown>;
  selection_scenario?: string;
  /** Optimization steps, not internal iterations. */
  num_steps?: number;
  train_config?: WorkflowTrainConfig;
  state_machine: WorkflowStateMachineSpec;
}

// ─── Job DTO ────────────────────────────────────────────────────────────────

export interface WorkflowRunnerProgress {
  iteration?: number;
  max_iterations?: number;
  stage?: string;
  substage?: string;
  progress_percent?: number;
  message?: string;
}

/** Identifies the snapshot the proposal is bound to — and the write lock for applying it. */
export interface WorkflowBaseRevision {
  digest: string;
  workflow_version_id?: string;
  workflow_version?: number;
  stand?: string;
}

export interface WorkflowTuningSummary {
  iterations?: number;
  /** What the algorithm *proposed*; selection happens later and may reject all of it. */
  proposed_states_changed?: number;
  proposed_roles_changed?: number;
  /** `null` is "no selection information", a third state — not a synonym for `false`. */
  candidate_accepted?: boolean | null;
  tokens?: number;
  errors?: number;
  patch_limit_breached?: boolean;
}

export interface WorkflowTuningScores {
  initial?: number;
  best?: number;
  delta?: number;
  improved?: boolean;
}

/**
 * Точка валидационной кривой прогона (AIT-291/AIT-292).
 *
 * Приходит только на GET одиночной джобы: в списке поля нет и не будет — двадцать
 * джоб не должны тащить двадцать кривых. Точки уже отсортированы по `iteration` и
 * уникальны по нему, пересортировывать не нужно; повторно доставленный после
 * рестарта бэка шаг обновляет свою точку, а не добавляет вторую на том же x.
 */
export interface WorkflowTuningStepPoint {
  iteration: number;
  /** `null` — «не измерено», а не ноль: в ряду это разрыв. */
  validation_score?: number | null;
  /** Качество само по себе, а не метрика отбора: `validation_score` хранит то, по
   *  чему шёл отбор (токены, стоимость...), и совпадает с качеством только когда
   *  `metric_id` джобы — `"quality"`. Отсутствует у джоб со старого бэка/раннера —
   *  тогда это разрыв в кривой качества, а не ноль на ней. */
  quality_score?: number | null;
  /** Опорная точка, от которой считается `scores.delta`. Ставится по `validation_only`
   *  от ML, поэтому признак именно этот, а не `iteration === 0`. */
  baseline?: boolean;
  config_sha256?: string;
  recorded_at?: string;
}

export interface WorkflowTuningJob {
  id: string;
  workflow_run_id?: string;
  user_id?: string;
  integration_id?: string;
  stand?: string;
  approach?: WorkflowTuningApproach | string;
  evaluation_mode?: string;
  status: WorkflowTuningJobStatus | string;
  phase?: string;
  progress_percent?: number;
  current_step?: number;
  /** `num_steps + 1` — base validation is step 0. */
  total_steps?: number;
  /** Echo of the requested scope; empty = the whole graph. */
  state_ids?: string[];
  runner_progress?: WorkflowRunnerProgress;
  base_revision?: WorkflowBaseRevision;
  summary?: WorkflowTuningSummary;
  /** Метрика отбора шагов ("quality", "actor_tokens", "actor_cost", ...) — с какой
   *  из них совпадает `validation_score` в `steps`. Отсутствует у джоб со старого
   *  бэка: тогда график качества по шагам просто не строится. */
  metric_id?: string;
  /** Only on GET of a single job, after it finishes. Never render `candidate_accepted` without this. */
  scores?: WorkflowTuningScores;
  /** Валидационные точки по шагам. Наполняется по ходу прогона, в отличие от
   *  `scores`, который извлекается из готового предложения и до конца отсутствует.
   *  Пустой список или отсутствующее поле — «данных нет»: у джоб, созданных до
   *  раскатки AIT-291, он пуст всегда, и это не ошибка. */
  steps?: WorkflowTuningStepPoint[];
  has_proposal?: boolean;
  error?: string | null;
  created_at?: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
}

export interface WorkflowTuningJobList {
  items: WorkflowTuningJob[];
  pagination?: { limit: number; offset: number; total: number };
}

// ─── Changes / proposal ─────────────────────────────────────────────────────

/** One changed path. `path` is a canonical target such as `/states/greeting/agents/coordinator/prompt`. */
export interface WorkflowTuningChange {
  path: string;
  before?: unknown;
  after?: unknown;
}

export interface WorkflowRuntimeTurns {
  planned?: number;
  completed?: number;
  failed?: number;
  limit?: number | null;
}

export interface WorkflowTuningProposal {
  base_revision: WorkflowBaseRevision;
  /** Every writable path — far larger than `changes_from_snapshot`. */
  resolved_targets?: string[];
  initial_source?: 'snapshot' | 'snapshot_with_overrides' | string;
  initial_projection?: Record<string, unknown>;
  selected_projection?: Record<string, unknown>;
  changes_from_initial?: WorkflowTuningChange[];
  changes_from_snapshot?: WorkflowTuningChange[];
  scores?: WorkflowTuningScores;
  runtime_turns?: WorkflowRuntimeTurns;
  usage?: Record<string, unknown>;
}

// ─── Journal ────────────────────────────────────────────────────────────────

/**
 * Counters for path keys the model mangled (a control character instead of the leading
 * slash, a stray space, capitalised `States`). A slice recovers what maps unambiguously
 * to one of its own paths and drops the rest — non-zero `discarded`/`rejected` is why a
 * slice that applied nothing can still look successful.
 */
export interface WorkflowSliceRecovery {
  applied?: number;
  recovered?: number;
  discarded?: number;
  missing?: number;
  rejected?: number;
}

export interface WorkflowSliceIteration {
  /** State id, or `__globals__` for the closing global-sections iteration. */
  slice: string;
  paths?: number;
  changed_paths?: number;
  tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  evidence?: { evidence?: { kept?: number; total?: number } };
  error?: string | null;
  recovery?: WorkflowSliceRecovery;
}

export interface WorkflowTuningJournal {
  summary?: WorkflowTuningSummary;
  slice_iterations?: WorkflowSliceIteration[];
  /** Non-empty means the limiter fired: the proposal holds the ORIGINAL workflow. */
  patch_limit_breaches?: unknown[];
  /** Non-empty with a non-empty `state_ids` means part of the request was never tuned. */
  /** Older ML builds returned strings; current builds may return structured target descriptors. */
  skipped_targets?: unknown[];
}

// ─── Usage ──────────────────────────────────────────────────────────────────

export interface WorkflowUsageRecord {
  operation?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  cached_tokens?: number;
  total_tokens?: number;
  total_cost?: number;
  records_count?: number;
}

export interface WorkflowTuningUsage {
  total?: WorkflowUsageRecord;
  operations?: WorkflowUsageRecord[];
}

// ─── Evaluation branch ──────────────────────────────────────────────────────

export interface WorkflowEvaluationJobRequest {
  integration_id: string;
  dataset_name: string | string[];
  batch_size?: number;
  evaluation_mode?: EvaluationMode | string;
  dataset_split?: string;
  batch_offset?: number;
  evaluator_params?: Record<string, unknown>;
  state_machine: WorkflowStateMachineSpec;
}

/** `result` is not flat: the score lives under `result.evaluation`, not at the root. */
export interface WorkflowEvaluationResult {
  status?: string;
  reason?: string;
  evaluator?: { kind?: string; params?: Record<string, unknown> };
  dataset?: { names?: string[]; split?: string; batch_size?: number; evaluated_items?: number };
  evaluation?: { metric_id?: string; score?: number; per_item_scores?: Record<string, unknown> };
  objectives?: Record<string, Record<string, number>>;
  usage?: Record<string, unknown>;
  /** The snapshot's config — large, and not meant for display. */
  config?: unknown;
}

export interface WorkflowEvaluationJob {
  id: string;
  integration_id?: string;
  evaluation_mode?: string;
  dataset_split?: string;
  status: string;
  phase?: string;
  progress_percent?: number;
  progress?: WorkflowRunnerProgress;
  result?: WorkflowEvaluationResult;
  error?: string | null;
  created_at?: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
}

export interface WorkflowEvaluationJobList {
  items: WorkflowEvaluationJob[];
  pagination?: { limit: number; offset: number; total: number };
}

// ─── Preview ────────────────────────────────────────────────────────────────

/** Что легло в основу оценки — считать её точной можно только по `states`/`steps`. */
export interface WorkflowTuningPreviewUnitCounts {
  states?: number;
  slices?: number;
  fields?: number;
  steps?: number;
  candidates?: number;
  dialogs_per_eval?: number;
  evaluations?: number;
  turns?: number;
}

/** Один из трёх слагаемых расхода — алгоритм, актор, судья. */
export interface WorkflowTuningPreviewComponent {
  label: string;
  input_tokens?: number;
  output_tokens?: number;
}

export interface WorkflowTuningPreviewBreakdown {
  unit_counts?: WorkflowTuningPreviewUnitCounts;
  components?: WorkflowTuningPreviewComponent[];
}

/** `POST /auto-tune/workflow/jobs/preview` — то же тело, что и запуск джобы. */
export interface WorkflowTuningPreview {
  expected_input_tokens?: number;
  expected_output_tokens?: number;
  expected_total_tokens?: number;
  predicted_total_cost_usd?: number | null;
  prediction_method?: string;
  warnings?: string[];
  breakdown?: WorkflowTuningPreviewBreakdown;
}

// ─── Errors ─────────────────────────────────────────────────────────────────

/**
 * `details` carries the ml-service's own diagnosis (`projection_slice_too_large`,
 * the slice, actual vs limit). Before this route existed every ml failure collapsed
 * into `503 auto-tune service error`, so surfacing it is the point.
 */
export interface WorkflowTuningErrorDetails {
  code?: string;
  slice?: string;
  actual?: number;
  limit?: number;
  [key: string]: unknown;
}

// ─── Terminal-status helper ─────────────────────────────────────────────────

const TERMINAL: ReadonlySet<string> = new Set(['succeeded', 'failed', 'cancelled']);

export function isWorkflowJobTerminal(status: string | undefined): boolean {
  return TERMINAL.has(String(status || '').toLowerCase());
}
